import {ChangeEvent, useId, useRef, useState} from "react";
import {Alert, Badge, Button, Card, ListGroup, ProgressBar} from "react-bootstrap";
import {SupportApi, SupportUploadProgress} from "../../api/SupportApi";
import {isReasonFor, SupportAttachmentViewModel, SupportTicketCategory, SupportTicketMessageViewModel, SupportTicketReasons, SupportTicketState, SupportTicketStateChange} from "../../api/models";
import {ErrorAlert} from "../../components/ErrorAlert";
import {formatBytes, formatDateTime} from "../../components/format";
import {addSupportFiles, SUPPORT_FILE_ACCEPT, SUPPORT_FILES_PER_MESSAGE} from "../../components/supportFiles";
import {announce} from "../../components/announce";
import {format, rich, support} from "../../i18n";

// Pieces shared by the customer's and the administrators' support pages.

const STATE_VARIANTS: Record<SupportTicketState, { bg: string, text?: 'dark' }> = {
    'pending': { bg: 'secondary' },
    'in-review': { bg: 'primary' },
    'waiting-for-info': { bg: 'warning', text: 'dark' },
    'resolved': { bg: 'success' },
    'closed': { bg: 'dark' }
};

export function SupportStateBadge({state, className}: { state: SupportTicketState, className?: string }) {
    const variant = STATE_VARIANTS[state] ?? { bg: 'secondary' };
    return <Badge bg={variant.bg} text={variant.text} className={className}>{support.states[state] ?? state}</Badge>;
}

export const reasonLabel = (id: string) => support.reasons[id] ?? id;

// The reasons an administrator can give for changing to the state.
// A press inquiry has its own reasons (isReasonFor).
export function adminReasonsFor(state: SupportTicketState | '', category?: SupportTicketCategory) {
    return SupportTicketReasons.filter(x => x.state === state && x.actor === 'admin' && isReasonFor(x, category));
}

// Picks up to 5 PNG, JPG or TXT files, each at most 10 MB, and says why any were left out.
export function AttachmentPicker({files, onChange, disabled}: { files: File[], onChange(files: File[]): void, disabled?: boolean }) {
    const id = useId();
    const input = useRef<HTMLInputElement>(null);
    const [problems, setProblems] = useState<string[]>([]);

    const add = (e: ChangeEvent<HTMLInputElement>) => {
        const result = addSupportFiles(files, Array.from(e.target.files ?? []));
        setProblems(result.problems);
        if (result.problems.length) {
            announce(result.problems.join(' '));
        }
        onChange(result.files);
        // So choosing the same file again (after removing it) still fires onChange.
        e.target.value = '';
    };

    const remove = (file: File) => {
        setProblems([]);
        onChange(files.filter(x => x !== file));
        announce(format(support.attachments.removed, { name: file.name }));
        input.current?.focus();
    };

    return (
        <div>
            <label htmlFor={id} className="form-label">{rich(support.attachments.label, { muted: text => <span className="text-body-secondary">{text}</span> })}</label>
            <input ref={input} id={id} type="file" className="form-control" multiple accept={SUPPORT_FILE_ACCEPT} onChange={add}
                   disabled={disabled || files.length >= SUPPORT_FILES_PER_MESSAGE} aria-describedby={`${id}-help`}/>
            <div id={`${id}-help`} className="form-text">
                {format(support.attachments.help, { max: SUPPORT_FILES_PER_MESSAGE })}
            </div>
            {problems.length > 0 && (
                <Alert variant="warning" className="mt-2 mb-0 py-2" dismissible onClose={() => setProblems([])}>
                    {problems.map(x => <div key={x}>{x}</div>)}
                </Alert>
            )}
            {files.length > 0 && (
                <ListGroup className="mt-2">
                    {files.map(file => (
                        <ListGroup.Item key={`${file.name}-${file.size}`} className="d-flex align-items-center gap-2">
                            <AttachmentIcon contentType={file.type}/>
                            <span className="text-break">{file.name}</span>
                            <span className="text-body-secondary small text-nowrap">{formatBytes(file.size)}</span>
                            <Button size="sm" variant="link" className="ms-auto link-danger" onClick={() => remove(file)} disabled={disabled}
                                    aria-label={format(support.attachments.remove, { name: file.name })}>
                                <i className="bi bi-x-lg" aria-hidden="true"></i>
                            </Button>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </div>
    );
}

// Overall upload progress across the files being sent, by size.
export function useUploadProgress() {
    const [progress, setProgress] = useState<Map<File, number>>();

    const onProgress = ({file, fraction}: SupportUploadProgress) => setProgress(current => new Map(current).set(file, fraction));
    const start = (files: File[]) => setProgress(files.length ? new Map(files.map(x => [x, 0])) : undefined);
    const done = () => setProgress(undefined);

    let fraction: number | undefined;
    if (progress) {
        const total = [...progress.keys()].reduce((sum, x) => sum + x.size, 0);
        fraction = total ? [...progress.entries()].reduce((sum, [file, f]) => sum + file.size * f, 0) / total : 0;
    }
    return { fraction, onProgress, start, done };
}

export function UploadProgress({fraction}: { fraction?: number }) {
    if (fraction === undefined) {
        return null;
    }
    const percent = Math.round(fraction * 100);
    return <ProgressBar now={percent} label={`${percent}%`} className="mt-3" aria-label={support.attachments.uploading}/>;
}

function AttachmentIcon({contentType}: { contentType: string }) {
    return <i className={`bi ${contentType.startsWith('image/') ? 'bi-file-earmark-image' : 'bi-file-earmark-text'}`} aria-hidden="true"></i>;
}

// The ticket's messages, oldest first. Their text is shown as text: never as HTML.
// customerName: for a ticket opened signed out, whose messages have no username.
export function MessageThread({ticketId, messages, showAuthors, customerName}: { ticketId: string, messages: SupportTicketMessageViewModel[], showAuthors: boolean, customerName?: string }) {
    const [downloading, setDownloading] = useState<string>();
    const [error, setError] = useState<unknown>();

    const download = async (attachment: SupportAttachmentViewModel) => {
        setDownloading(attachment.id);
        setError(undefined);
        try {
            const target = await SupportApi.downloadUrl(ticketId, attachment.id);
            window.location.assign(target.url);
        } catch (err) {
            setError(err);
        } finally {
            setDownloading(undefined);
        }
    };

    return (
        <>
            <ErrorAlert error={error} onClose={() => setError(undefined)} messages={{ 404: support.thread.fileGone }}/>
            <ol className="list-unstyled mb-0" aria-label={support.thread.messages}>
                {messages.map((message, index) => (
                    <li key={message.id} className="mb-3">
                        <Card className={message.fromSupport ? 'support-message-staff' : undefined}>
                            <Card.Header className="d-flex flex-wrap align-items-center gap-2 small">
                                <strong>
                                    {message.fromSupport ? support.thread.support : message.authorUsername || customerName || support.thread.visitor}
                                    {message.fromSupport && showAuthors && <span className="fw-normal text-body-secondary">{format(support.thread.supportAuthor, { username: message.authorUsername })}</span>}
                                </strong>
                                {index === 0 && <Badge bg="secondary">{support.thread.synopsis}</Badge>}
                                <span className="ms-auto text-body-secondary">{formatDateTime(message.createdOn)}</span>
                            </Card.Header>
                            <Card.Body>
                                <div className="support-message">{message.body}</div>
                                {message.attachments.length > 0 && (
                                    <ul className="list-unstyled d-flex flex-wrap gap-2 mt-3 mb-0" aria-label={support.thread.attachments}>
                                        {message.attachments.map(attachment => (
                                            <li key={attachment.id}>
                                                <Button size="sm" variant="outline-secondary" onClick={() => download(attachment)} disabled={downloading === attachment.id}>
                                                    <AttachmentIcon contentType={attachment.contentType}/>
                                                    <span className="ms-1">{attachment.fileName}</span>
                                                    <span className="visually-hidden">{support.thread.download}</span>
                                                    <span className="ms-1 text-body-secondary">{formatBytes(attachment.size)}</span>
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card.Body>
                        </Card>
                    </li>
                ))}
            </ol>
        </>
    );
}

export function StateHistory({history}: { history: SupportTicketStateChange[] }) {
    return (
        <ol className="list-unstyled small mb-0">
            {[...history].reverse().map((change, index) => (
                <li key={index} className="mb-2">
                    <div><SupportStateBadge state={change.to}/> <span>{reasonLabel(change.reason)}</span></div>
                    <div className="text-body-secondary">
                        {change.by === 'SYSTEM'
                            ? format(support.history.automatically, { date: formatDateTime(change.on) })
                            : format(support.history.by, { date: formatDateTime(change.on), username: change.by })}
                    </div>
                </li>
            ))}
        </ol>
    );
}
